// Appointments: backend rows → the design's vocabulary (state/appointments.tsx),
// plus the few API calls the Appointments screen and its drawers share.
// Row mapping builds on lib/home.js (toAppointment), so Home and this screen
// read appointments the same way.
import axios from "axios";
import config from "../../../config";
import { apiGet, fetchAllPages, fmtPhone } from "../crm/contactsKit";
import { DAY, ymd } from "../../lib/format";
import { toAppointment } from "../../lib/home";
import { fetchApi } from "../marketing/access";

// "Requested" is a booking (Olli's AI agent, an on-call slot) the clinic still
// has to confirm; "In consultation" is the old app's in_progress, set by
// "Start consultation". Both were read as Booked / Checked in before.
export const APPOINTMENT_STATUSES = ["Requested", "Booked", "Confirmed", "Checked in", "In consultation", "Completed", "No-show", "Cancelled"];
/** The design's statuses → appointments.status. */
export const STATUS_KEY = { Requested: "requested", Booked: "scheduled", Confirmed: "confirmed", "Checked in": "checked_in", "In consultation": "in_progress", Completed: "completed", "No-show": "no_show", Cancelled: "cancelled" };
/** appointments.booking_source → the design's "booked by". */
const SOURCE = { ai_agent: "Olli AI", phone: "Front desk", walk_in: "Walk-in", website: "Website", app: "Website", referral: "Referral", recurring: "Recurring booking" };

/* Visit details the old booking form had (appointments columns). */
/** appointment_type → label. */
export const APPOINTMENT_TYPES = [["consultation", "Consultation"], ["follow_up", "Follow-up"], ["procedure", "Procedure"], ["telemedicine", "Telemedicine"], ["emergency", "Emergency"], ["lab_test", "Lab test"], ["other", "Other"]];
/** priority → label. */
export const PRIORITIES = [["normal", "Normal"], ["urgent", "Urgent"], ["emergency", "Emergency"]];
/** booking_source a person can pick ("Olli AI" bookings come from the agent itself). */
export const BOOKING_SOURCES = [["phone", "Phone"], ["walk_in", "Walk-in"], ["website", "Website"], ["referral", "Referral"]];
export const labelOf = (pairs, key) => pairs.find(([k]) => k === key)?.[1] || "";
/** Vitals stored as appointments.vitals (JSON): key, label, unit. */
export const VITALS = [["bp", "Blood pressure", "mmHg"], ["pulse", "Pulse", "bpm"], ["temperature", "Temperature", "°F"], ["spo2", "SpO2", "%"], ["weight", "Weight", "kg"], ["height", "Height", "cm"]];
const vitalsOf = (value) => {
  let v = value;
  if (typeof v === "string") { try { v = JSON.parse(v); } catch (_) { v = null; } }
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  // Old rows used vitals_bp / temp style keys.
  const pick = (...keys) => keys.map((k) => v[k]).find((x) => x !== undefined && x !== null && x !== "");
  return Object.fromEntries(VITALS.map(([key]) => [key, pick(key, `vitals_${key}`, key === "temperature" ? "temp" : key)]).filter(([, x]) => x !== undefined).map(([k, x]) => [k, String(x)]));
};
const LOCAL_STATUS = { requested: "Requested", in_progress: "In consultation" };
const clockOf = (t) => (t ? String(t).slice(0, 5) : "");

// The last reminder's WhatsApp state (item 173): row.reminder = { status, sent_at } | null.
const REMINDER_LABEL = { sent: "Sent", delivered: "Delivered", read: "Read", failed: "Failed" };

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
/** Any write; resolves to { ok, body, status } so callers can read a 409's details. */
export const apiSend = (method, path, data) =>
  axios({ method, url: `${config.API_BASE_URL}${path}`, data, ...auth() })
    .then((r) => ({ ok: true, body: r.data, status: r.status }))
    .catch((e) => ({ ok: false, body: e.response?.data || null, status: e.response?.status || 0, message: e.response?.data?.message || "Something went wrong. Try again." }));

/** An /api/appointments row in the design's shape (plus what this screen needs). */
export function toAppt(row) {
  const base = toAppointment(row);
  return {
    ...base,
    status: LOCAL_STATUS[row.status] || base.status,
    // Visit details (old AppointmentForm / AppointmentDetails).
    type: row.appointment_type || "consultation",
    priority: row.priority || "normal",
    bookingSource: row.booking_source || "phone",
    chiefComplaint: row.chief_complaint || "",
    internalNotes: row.internal_notes || "",
    cancellationReason: row.cancellation_reason || "",
    vitals: vitalsOf(row.vitals),
    checkedInAt: row.checked_in_at ? new Date(row.checked_in_at).getTime() : null,
    consultStart: clockOf(row.actual_start_time),
    consultEnd: clockOf(row.actual_end_time),
    code: row.appointment_number || `#${row.id}`,
    rawStatus: row.status,
    facilityId: row.facility_id,
    patientPhone: row.patient_phone || "",
    reminder: row.reminder ? REMINDER_LABEL[row.reminder.status] || "Sent" : row.reminder_sent ? "Sent" : "Not sent",
    reminderAt: row.reminder?.sent_at ? new Date(row.reminder.sent_at).getTime() : null,
    // A chair number at the location (item 174; locations store a chair count).
    chairId: row.chair_id ?? null,
    // The lead this visit was booked from (item 176).
    leadId: row.lead_id ?? null,
    source: SOURCE[row.booking_source] || "Front desk",
    createdAt: row.created_at ? new Date(row.created_at).getTime() : null,
    notes: row.notes || "",
    purpose: row.purpose || "",
  };
}

/** Rescheduled rows are the old half of a move; the new appointment carries the visit. */
export const visible = (row) => row.status !== "rescheduled" && row.start !== null;

/** Every appointment between two local days (inclusive), all pages. `null` when the call fails. */
export async function loadAppointments({ from, to, facilityId, doctorId, patientId, order = "ASC", maxPages = 10 }) {
  const params = { sort_by: "appointment_date", sort_order: order };
  if (from !== undefined && from !== null) params.date_from = ymd(from);
  if (to !== undefined && to !== null) params.date_to = ymd(to);
  if (facilityId) params.facility_id = facilityId;
  if (doctorId) params.doctor_id = doctorId;
  if (patientId) params.patient_id = patientId;
  const rows = await fetchAllPages("/api/appointments", params, maxPages);
  return rows ? rows.map(toAppt).filter(visible) : null;
}

/**
 * The same as loadAppointments, but a failure keeps its reason:
 * { rows } or { rows: null, failure } where failure is a fetchApi result.
 */
export async function loadAppointmentsChecked({ from, to, facilityId, order = "ASC", maxPages = 10 }) {
  const params = { sort_by: "appointment_date", sort_order: order, limit: 100 };
  if (from !== undefined && from !== null) params.date_from = ymd(from);
  if (to !== undefined && to !== null) params.date_to = ymd(to);
  if (facilityId) params.facility_id = facilityId;
  const out = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const r = await fetchApi("/api/appointments", { params: { ...params, page } });
    if (!r.ok) return page === 1 ? { rows: null, failure: r } : { rows: out.map(toAppt).filter(visible) };
    out.push(...(r.data?.data || []));
    if (page >= (r.data?.pagination?.pages || 1)) break;
  }
  return { rows: out.map(toAppt).filter(visible) };
}

/* ---------- Automatic reminders (services/appointmentReminderScheduler) ---------- */

/**
 * GET /api/appointments/reminder-settings → [{ id, name, hours_before, send_whatsapp, template, is_active }].
 * Resolves to { ok, reminders, failure }.
 */
export async function loadReminderSettings() {
  const r = await fetchApi("/api/appointments/reminder-settings");
  return r.ok ? { ok: true, reminders: r.data?.data?.reminders || [] } : { ok: false, reminders: [], failure: r };
}

/** PUT /api/appointments/reminder-settings { reminders: [{ name, hours_before, template, is_active }] } (owner / admin only). */
export function saveReminderSettings(reminders) {
  return fetchApi("/api/appointments/reminder-settings", { method: "put", body: { reminders } });
}

/** "24 hours", "2 hours", "1 hour", "2 days". */
export const hoursLabel = (h) => (Number(h) % 24 === 0 && Number(h) >= 48 ? `${Number(h) / 24} days` : `${h} hour${Number(h) === 1 ? "" : "s"}`);

/** Doctors (staff with role doctor). `id` is users.id, which appointments and schedules use. */
export async function loadDoctors(facilityId) {
  const r = await apiGet("/api/staff", { role: "doctor", is_active: "true", ...(facilityId ? { facility_id: facilityId } : {}) });
  if (!r?.success) return [];
  const seen = new Set();
  return (r.data || [])
    .map((s) => ({ id: s.user_id, name: s.user_name || [s.first_name, s.last_name].filter(Boolean).join(" ") || "Doctor" }))
    .filter((d) => d.id && !seen.has(d.id) && seen.add(d.id));
}

/** /api/facility-schedules rows, by facility id and JS weekday. */
export async function loadFacilityHours() {
  const r = await apiGet("/api/facility-schedules");
  const map = {};
  (r?.success ? r.data || [] : []).forEach((row) => {
    if (row.is_active === 0) return;
    map[row.facility_id] = map[row.facility_id] || {};
    map[row.facility_id][Number(row.day_of_week)] = row;
  });
  return map;
}

const toMin = (t) => { const [h, m] = String(t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };

/** The facility's break on that day, in minutes, or null. */
export function breakOn(hours, facilityId, dayMs) {
  const row = facilityId ? hours?.[facilityId]?.[new Date(dayMs).getDay()] : null;
  if (!row || !row.is_open || !row.break_start || !row.break_end) return null;
  return { start: toMin(row.break_start), end: toMin(row.break_end) };
}

/** First bookable slot at or after `from` (GET /api/appointments/available-slots), up to 14 days ahead. */
export async function nextFreeSlot(doctorId, facilityId, from) {
  if (!doctorId) return null;
  const firstDay = new Date(from).setHours(0, 0, 0, 0);
  for (let i = 0; i < 14; i++) {
    const day = firstDay + i * DAY;
    const r = await apiGet("/api/appointments/available-slots", { doctor_id: doctorId, date: ymd(day), ...(facilityId ? { facility_id: facilityId } : {}) });
    const times = (r?.success ? r.data || [] : []).map((s) => day + toMin(s.time) * 60_000).filter((t) => t >= from).sort((a, b) => a - b);
    if (times.length) return times[0];
  }
  return null;
}

/** Patients for the picker: { value, label, hint, name, phone }. */
export async function loadPatients() {
  const rows = await fetchAllPages("/api/patients", { status: "active", sort_by: "first_name", sort_order: "ASC" }, 10);
  return (rows || []).map((p) => {
    const name = [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "Unnamed patient";
    return { value: String(p.id), label: name, hint: p.phone ? fmtPhone(p.phone) : "", name, phone: p.phone || "" };
  });
}

/** Treatment names from the clinic's catalogue (GET /api/treatments). */
export async function loadServices() {
  const r = await apiGet("/api/treatments", { limit: 200 });
  return r?.success ? [...new Set((r.data || []).map((t) => t.name).filter(Boolean))] : [];
}
